import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import {
  buildDeeplinkUrl,
  mobileContractScreenIdSchema,
  screenParamsSchemas,
  type MobileContractScreenId,
} from '../screens';
import { ianaTimezoneSchema } from '../timezone';

// Re-exported so existing reminder consumers keep compiling (ADR-095 Rule 1) —
// the canonical declaration lives in ../timezone alongside DEFAULT_TIME_ZONE
// and resolveTimeZone.
export { ianaTimezoneSchema };

export const reminderTimeSchema = z
  .string()
  .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/, 'time must be 24h HH:MM');

// The wall-clock time a reminder takes when it is switched on with no stored
// schedule. Server-owned so the API can seed it when a member enables a
// reminder that has never been scheduled; clients read the same value.
export const DEFAULT_REMINDER_TIME = '18:00';

export const REMINDER_DEFINITION_STATUS = ['draft', 'active', 'retired'] as const;
export const reminderDefinitionStatusSchema = z.enum(REMINDER_DEFINITION_STATUS);

export const REMINDER_SCHEDULE_CAPABILITY = ['daily_time'] as const;
export const reminderScheduleCapabilitySchema = z.enum(REMINDER_SCHEDULE_CAPABILITY);

export const reminderDestinationSchema = z
  .object({
    screenId: mobileContractScreenIdSchema,
    params: z.record(z.string(), z.string()).default({}),
  })
  .strict()
  .superRefine((destination, ctx) => {
    const parsed = screenParamsSchemas[destination.screenId].strict().safeParse(destination.params);
    if (!parsed.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['params'],
        message: 'params do not match the ScreenId contract',
      });
    }
  });
export type ReminderDestination = z.infer<typeof reminderDestinationSchema>;

/** Proves a persisted destination can be converted without platform route knowledge. */
export function buildReminderDeeplink(destination: ReminderDestination): string {
  const parsed = reminderDestinationSchema.parse(destination);
  return buildDeeplinkUrl(parsed.screenId, parsed.params);
}

export const reminderDefinitionConfigSchema = z
  .object({
    multiplicity: z.enum(['one_per_member', 'many']).default('many'),
    scheduleCapabilities: z.array(reminderScheduleCapabilitySchema).min(1),
    contentTemplateRef: z.string().min(1).max(128),
    destination: z
      .object({
        mode: z.enum(['none', 'optional', 'required']),
        default: reminderDestinationSchema.nullable().default(null),
      })
      .strict()
      .superRefine((value, ctx) => {
        // A definition that forbids destinations must not smuggle one in via
        // its default — the capability would persist it on every write.
        if (value.mode === 'none' && value.default !== null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['default'],
            message: "destination.default must be null when mode is 'none'",
          });
        }
      }),
    relatedResourceResolvers: z.array(z.string().min(1).max(64)).default([]),
  })
  .strict();
export type ReminderDefinitionConfig = z.infer<typeof reminderDefinitionConfigSchema>;

export const dailyTimeScheduleSchema = z
  .object({
    version: z.literal(1),
    type: z.literal('daily_time'),
    time: reminderTimeSchema,
    timezone: ianaTimezoneSchema,
  })
  .strict();
export type DailyTimeSchedule = z.infer<typeof dailyTimeScheduleSchema>;
export const reminderScheduleSchema = z.discriminatedUnion('type', [dailyTimeScheduleSchema]);
export type ReminderSchedule = z.infer<typeof reminderScheduleSchema>;

export const reminderRelatedResourceSchema = z
  .object({
    resolver: z.string().min(1).max(64),
    id: z.string().uuid(),
  })
  .strict();
export type ReminderRelatedResource = z.infer<typeof reminderRelatedResourceSchema>;

const reminderMutationFields = {
  enabled: z.boolean(),
  schedule: reminderScheduleSchema.nullable(),
  destination: reminderDestinationSchema.nullable().default(null),
  relatedResource: reminderRelatedResourceSchema.nullable().default(null),
} as const;

function requireEnabledSchedule(
  value: { enabled: boolean; schedule: ReminderSchedule | null },
  ctx: z.RefinementCtx,
): void {
  if (value.enabled && value.schedule === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['schedule'],
      message: 'an enabled reminder requires a schedule',
    });
  }
}

export const reminderCreateRequestSchema = z
  .object({
    definitionKey: z.string().min(1).max(64),
    preserveExisting: z.boolean().optional(),
    ...reminderMutationFields,
  })
  .strict()
  .superRefine(requireEnabledSchedule);
export type ReminderCreateRequest = z.infer<typeof reminderCreateRequestSchema>;

export const reminderReplaceRequestSchema = z
  .object(reminderMutationFields)
  .strict()
  .superRefine(requireEnabledSchedule);
export type ReminderReplaceRequest = z.infer<typeof reminderReplaceRequestSchema>;

export const reminderDefinitionProjectionSchema = z
  .object({
    key: z.string(),
    version: z.number().int().positive(),
  })
  .strict();

export const reminderDataSchema = z
  .object({
    id: z.string().uuid(),
    definition: reminderDefinitionProjectionSchema,
    enabled: z.boolean(),
    schedule: reminderScheduleSchema.nullable(),
    destination: reminderDestinationSchema.nullable(),
    relatedResource: reminderRelatedResourceSchema.nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type ReminderData = z.infer<typeof reminderDataSchema>;

export const reminderResponseSchema = apiEnvelope(reminderDataSchema);
export const reminderListResponseSchema = apiEnvelope(z.array(reminderDataSchema));

export const reminderIdParamsSchema = z.object({ reminderId: z.string().uuid() }).strict();

/** Presentation adapter retained for the existing AgentScreen/weekly-plan card. */
export const checkInReminderDataSchema = z.discriminatedUnion('enabled', [
  z.object({
    enabled: z.literal(true),
    reminderTime: reminderTimeSchema,
    timezone: ianaTimezoneSchema,
  }),
  z.object({ enabled: z.literal(false), reminderTime: z.null(), timezone: z.null() }),
]);
export type CheckInReminderData = z.infer<typeof checkInReminderDataSchema>;

export function validateReminderDestinationForScreen(
  screenId: MobileContractScreenId,
  params: Record<string, string>,
): ReminderDestination {
  return reminderDestinationSchema.parse({ screenId, params });
}
